import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Users, Building2, FileText, PlusCircle, LogOut, Menu, X, Package, Palette,
  BarChart3, ChevronsLeft, ChevronsRight, UserCog, UserCircle2, CreditCard, Sparkles, ChevronDown,
  Sliders, Briefcase, Cog
} from "lucide-react";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";

// Estructura del menú: cada grupo tiene items, y cada item puede tener roles/feature.
// Grupos con `items` = submenú colapsable. Sin `items` = link directo.
const MENU = [
  { key: "dashboard", to: "/app", icon: LayoutDashboard, label: "Dashboard", end: true },
  {
    key: "params",
    icon: Sliders,
    label: "Parámetros",
    items: [
      { to: "/app/products", icon: Package, label: "Productos y Servicios", roles: ["owner", "administrador"] },
      { to: "/app/clients", icon: Users, label: "Clientes" },
      { to: "/app/templates", icon: Palette, label: "Templates", roles: ["owner", "administrador"] },
    ],
  },
  {
    key: "quotations",
    icon: FileText,
    label: "Cotizaciones",
    items: [
      { to: "/app/quotations/new", icon: PlusCircle, label: "Nueva Cotización" },
      { to: "/app/quotations", icon: FileText, label: "Mis Cotizaciones" },
      { to: "/app/pipeline", icon: BarChart3, label: "Pipeline CRM", feature: "pipeline" },
    ],
  },
  {
    key: "management",
    icon: Briefcase,
    label: "Gestión",
    items: [
      { to: "/app/insights", icon: Sparkles, label: "Cotflow Insights", feature: "insights" },
    ],
  },
  {
    key: "settings",
    icon: Cog,
    label: "Configuración",
    items: [
      { to: "/app/company", icon: Building2, label: "Mi Empresa", roles: ["owner", "administrador"] },
      { to: "/app/team", icon: UserCog, label: "Equipo", roles: ["owner", "administrador", "supervisor"], feature: "user_quota_gt1" },
      { to: "/app/plan", icon: CreditCard, label: "Mi Plan" },
      { to: "/app/profile", icon: UserCircle2, label: "Mi Perfil" },
    ],
  },
];

function isItemVisible(item, user, plan) {
  if (item.roles && !item.roles.includes(user?.role)) return false;
  if (item.feature === "pipeline" && !plan?.features?.pipeline) return false;
  if (item.feature === "insights" && (plan?.features?.insights_per_month || 0) === 0) return false;
  if (item.feature === "user_quota_gt1" && (plan?.features?.user_quota || 1) <= 1) return false;
  return true;
}

function SidebarLink({ to, icon: Icon, label, end, collapsed, onNav, indent = false, testid }) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onNav}
      title={label}
      data-testid={testid}
      className={({ isActive }) =>
        `flex items-center ${collapsed ? "justify-center px-2" : indent ? "pl-9 pr-3" : "px-3"} gap-3 py-2 rounded-lg text-sm font-medium transition-all ${
          isActive ? "bg-blue-50 text-blue-700" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
        }`
      }
    >
      <Icon size={indent && !collapsed ? 15 : 17} className="flex-shrink-0" />
      {!collapsed && <span className="truncate">{label}</span>}
    </NavLink>
  );
}

function SidebarGroup({ group, collapsed, user, plan, onNav, location }) {
  const visibleItems = group.items.filter((it) => isItemVisible(it, user, plan));
  const isActiveGroup = visibleItems.some((it) => location.pathname === it.to || (it.to !== "/app" && location.pathname.startsWith(it.to)));
  const storageKey = `cotflow_menu_${group.key}`;
  // Default expanded; persiste por grupo lo que el usuario abre/cierra
  const [open, setOpen] = useState(() => {
    const saved = localStorage.getItem(storageKey);
    if (saved === null) return true;
    return saved === "open";
  });

  useEffect(() => { if (isActiveGroup) setOpen(true); }, [isActiveGroup]);

  const toggleOpen = () => {
    const next = !open;
    setOpen(next);
    localStorage.setItem(storageKey, next ? "open" : "closed");
  };

  if (visibleItems.length === 0) return null;

  // Modo colapsado: renderizar solo los items (sin header de grupo, con separador)
  if (collapsed) {
    return (
      <div className="border-t border-slate-100 pt-2 first:border-t-0 first:pt-0">
        {visibleItems.map((it) => (
          <SidebarLink key={it.to} to={it.to} icon={it.icon} label={it.label} collapsed onNav={onNav}
            testid={`nav-${it.to.replace(/\//g, "-")}`} />
        ))}
      </div>
    );
  }

  const GroupIcon = group.icon;
  return (
    <div>
      <button
        onClick={toggleOpen}
        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${
          isActiveGroup ? "text-blue-700" : "text-slate-500 hover:text-slate-800"
        }`}
        data-testid={`nav-group-${group.key}`}
      >
        <GroupIcon size={15} className="flex-shrink-0" />
        <span className="flex-1 text-left">{group.label}</span>
        <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-0.5 space-y-0.5">
          {visibleItems.map((it) => (
            <SidebarLink key={it.to} to={it.to} icon={it.icon} label={it.label} onNav={onNav} indent
              testid={`nav-${it.to.replace(/\//g, "-")}`} />
          ))}
        </div>
      )}
    </div>
  );
}

function NavContent({ collapsed, onNav }) {
  const location = useLocation();
  const { user, plan } = useAuth();
  return (
    <div className="space-y-2">
      {MENU.map((entry) => {
        if (entry.items) return <SidebarGroup key={entry.key} group={entry} collapsed={collapsed} user={user} plan={plan} onNav={onNav} location={location} />;
        return <SidebarLink key={entry.key} to={entry.to} icon={entry.icon} label={entry.label} end={entry.end}
          collapsed={collapsed} onNav={onNav} testid={`nav-${entry.to.replace(/\//g, "-")}`} />;
      })}
    </div>
  );
}

export default function Layout() {
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("cotflow_sidebar") === "collapsed");
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem("cotflow_sidebar", next ? "collapsed" : "expanded");
  };

  return (
    <div className="min-h-screen flex bg-slate-100/60">
      {/* Desktop sidebar */}
      <aside className={`hidden lg:flex ${collapsed ? "w-[68px]" : "w-64"} flex-col bg-white border-r border-slate-200/80 fixed h-full z-30 shadow-sm transition-all duration-200`}>
        <div className={`${collapsed ? "px-3" : "px-5"} py-5 border-b border-slate-100 flex items-center ${collapsed ? "justify-center" : "justify-between"}`}>
          {!collapsed && (
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/app")}>
              <img src="/logo.png" alt="CotflowAI" className="w-7 h-7 object-contain" />
              <span className="font-black text-xl tracking-tight text-slate-900 font-['Satoshi']">CotflowAI</span>
            </div>
          )}
          <button onClick={toggleCollapsed} className="p-1.5 hover:bg-slate-100 rounded-lg transition text-slate-400 hover:text-slate-600" data-testid="sidebar-toggle">
            {collapsed ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
          </button>
        </div>
        <nav className="flex-1 px-2 py-4 overflow-y-auto">
          <NavContent collapsed={collapsed} />
        </nav>
        <div className="px-2 py-3 border-t border-slate-200">
          {!collapsed && user && (
            <div className="px-3 pb-2 text-xs text-slate-500 truncate">
              <div className="font-medium text-slate-700 truncate">{user.name}</div>
              <div className="truncate">{user.email}</div>
            </div>
          )}
          <button onClick={logout} title="Cerrar sesión" className={`flex items-center ${collapsed ? "justify-center" : ""} gap-3 ${collapsed ? "px-2" : "px-3"} py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-red-50 hover:text-red-600 w-full transition-all`} data-testid="logout-btn">
            <LogOut size={18} className="flex-shrink-0" />{!collapsed && "Cerrar sesión"}
          </button>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 inset-x-0 h-14 bg-white border-b border-slate-200 flex items-center px-4 z-40 shadow-sm">
        <button onClick={() => setOpen(!open)} className="p-2 -ml-2 text-slate-600"><Menu size={20} /></button>
        <img src="/logo.png" alt="CotflowAI" className="w-6 h-6 object-contain ml-3" />
        <span className="ml-2 font-black text-lg text-slate-900 font-['Satoshi']">CotflowAI</span>
      </div>
      {open && <div className="lg:hidden fixed inset-0 z-50 bg-black/30" onClick={() => setOpen(false)}>
        <aside className="w-72 bg-white h-full shadow-lg overflow-y-auto" onClick={e => e.stopPropagation()}>
          <div className="px-5 py-5 border-b border-slate-100 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="CotflowAI" className="w-7 h-7 object-contain" />
              <span className="font-black text-xl text-slate-900 font-['Satoshi']">CotflowAI</span>
            </div>
            <button onClick={() => setOpen(false)}><X size={20} /></button>
          </div>
          <nav className="px-3 py-4">
            <NavContent collapsed={false} onNav={() => setOpen(false)} />
          </nav>
          <div className="p-3 border-t border-slate-200">
            <button onClick={logout} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-red-50 hover:text-red-600 w-full"><LogOut size={18} /> Cerrar sesión</button>
          </div>
        </aside>
      </div>}

      <main className={`flex-1 ${collapsed ? "lg:ml-[68px]" : "lg:ml-64"} p-6 pt-20 lg:pt-6 transition-all duration-200`}>
        <Outlet />
      </main>
    </div>
  );
}
